import { Component, ElementRef, EmbeddedViewRef, EventEmitter, HostListener, Input, OnDestroy, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { HyperpureSheetCartService, HyperpureSheetLine, HyperpureSheetSearchRow, HyperpureSheetStatus, HyperpureSheetSummary } from '../../services/hyperpure-sheet-cart.service';

@Component({
    selector: 'app-hyperpure-sheet-upload',
    templateUrl: './hyperpure-sheet-upload.component.html',
    styleUrls: ['./hyperpure-sheet-upload.component.scss']
})
export class HyperpureSheetUploadComponent implements OnDestroy {

    @Input() entityId = '';
    @Input() variant: 'bar' | 'icon' = 'bar';
    @Output() cartChanged = new EventEmitter<void>();
    @Output() rowsReady = new EventEmitter<HyperpureSheetSearchRow[]>();

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
    @ViewChild('sheetModal') sheetModal: TemplateRef<void>;

    dragOver = false;
    phase: 'idle' | 'reading' | 'matching' | 'writing' | 'done' | 'error' = 'idle';
    done = 0;
    total = 1;
    label = 'Reading the sheet';
    errorMessage = '';
    summary: HyperpureSheetSummary | null = null;
    showAlready = false;

    private cancelRequested = false;
    private modalView: EmbeddedViewRef<void> | null = null;

    constructor(
        private sheetCart: HyperpureSheetCartService,
        private viewContainerRef: ViewContainerRef
    ) { }

    get busy(): boolean {
        return this.phase === 'reading' || this.phase === 'matching' || this.phase === 'writing';
    }

    get progressPercent(): number {
        if (!this.total) {
            return 8;
        }
        return Math.max(8, Math.min(100, Math.round((this.done / this.total) * 100)));
    }

    get headline(): string {
        const summary = this.summary;
        if (!summary) {
            return '';
        }
        const placed = summary.added + summary.updated;
        if (placed > 0) {
            return 'Hyperpure cart updated';
        }
        if (summary.already > 0 && summary.notFound === 0 && summary.failed === 0) {
            return 'Your cart already matched this list';
        }
        return 'Nothing new was added';
    }

    get quietSkipCount(): number {
        return (this.summary?.lines || []).filter(line =>
            line.status === 'skipped' && line.detail.indexOf('No quantity') === 0
        ).length;
    }

    ngOnDestroy(): void {
        this.cancelRequested = true;
        this.destroyModal();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.modalView && this.phase !== 'writing') {
            this.close();
        }
    }

    openPicker(): void {
        if (this.busy) {
            return;
        }
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
            this.fileInput.nativeElement.click();
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        this.start(file || null);
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = true;
    }

    onDragLeave(): void {
        this.dragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
        const file = event.dataTransfer?.files && event.dataTransfer.files[0];
        this.start(file || null);
    }

    close(): void {
        if (this.phase === 'writing') {
            return;
        }
        this.cancelRequested = true;
        this.destroyModal();
    }

    lines(status: HyperpureSheetStatus): HyperpureSheetLine[] {
        return (this.summary?.lines || []).filter(line => line.status === status && !!line.item);
    }

    issueLines(): HyperpureSheetLine[] {
        return (this.summary?.lines || []).filter(line =>
            !!line.item && (line.status === 'not_found' || line.status === 'failed' || line.status === 'other_supplier' || this.isVisibleSkip(line))
        );
    }

    rowLabel(line: HyperpureSheetLine): string {
        if (!line.sheetRows.length) {
            return '';
        }
        if (line.sheetRows.length === 1) {
            return `Row ${line.sheetRows[0]}`;
        }
        return `Rows ${line.sheetRows.join(', ')}`;
    }

    private isVisibleSkip(line: HyperpureSheetLine): boolean {
        return line.status === 'skipped' && line.detail.indexOf('No quantity') !== 0 && !!line.item;
    }

    private async start(file: File | null): Promise<void> {
        if (!file || this.busy) {
            return;
        }
        const name = file.name.toLowerCase();
        if (!/\.(xlsx|xls|csv)$/.test(name)) {
            this.fail('Upload an Excel or CSV file with Item, Unit and Quantity columns.');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            this.fail('That file is too large. Keep the list under 2 MB.');
            return;
        }
        if (!this.entityId) {
            this.fail('Select an outlet before uploading a list.');
            return;
        }

        this.cancelRequested = false;
        this.summary = null;
        this.errorMessage = '';
        this.showAlready = false;
        this.phase = 'reading';

        try {
            const rows = await this.sheetCart.readSheet(file);
            if (this.cancelRequested) {
                this.phase = 'idle';
                this.destroyModal();
                return;
            }
            this.phase = 'idle';
            this.destroyModal();
            this.rowsReady.emit(rows);
        } catch (error) {
            if (this.cancelRequested) {
                this.phase = 'idle';
                this.destroyModal();
                return;
            }
            this.fail(error?.message || 'The upload could not be completed.');
        }
    }

    private fail(message: string): void {
        this.phase = 'error';
        this.errorMessage = message;
        this.openModal();
        this.modalView?.detectChanges();
    }

    private openModal(): void {
        if (!this.modalView) {
            this.modalView = this.viewContainerRef.createEmbeddedView(this.sheetModal);
            this.modalView.rootNodes.forEach(node => {
                if (node instanceof HTMLElement) {
                    document.body.appendChild(node);
                }
            });
        }
        this.modalView.detectChanges();
    }

    private destroyModal(): void {
        if (this.modalView) {
            this.modalView.destroy();
            this.modalView = null;
        }
    }
}
